import React from "react";
import type {
  Control,
  FieldValues,
  Path,
  RegisterOptions,
} from "react-hook-form";
import { Controller } from "react-hook-form";

import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

interface TextAreaProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  placeholder?: string;
  label?: string;
  rows?: number;
  className?: string;
  extraInfo?: string;
  rules?: Omit<
    RegisterOptions<T, Path<T>>,
    "disabled" | "setValueAs" | "valueAsNumber" | "valueAsDate"
  >;
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  disabled?: boolean;
  inputClassName?: string;
  require?: boolean;
  value?: string;
}

const TextAreaControl = <T extends FieldValues>({
  name,
  control,
  placeholder,
  label,
  className,
  extraInfo,
  rules,
  disabled,
  inputClassName,
  rows = 5,
  require,
  ...props
}: TextAreaProps<T>) => {
  const handleBlur = (
    e: React.FocusEvent<HTMLTextAreaElement>,
    rhfOnBlur: () => void,
  ) => {
    rhfOnBlur();
    props.onBlur?.(e);
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({
        field: { onChange: rhfOnChange, onBlur: rhfOnBlur, value, ref },
        fieldState: { error },
      }) => (
        <div className={className}>
          {label && (
            <label className="typo-label text-text-primary">
              {label} {require && <span className="text-red-500">*</span>}
            </label>
          )}
          <div className="relative pt-1">
            <Textarea
              ref={ref}
              value={value || ""}
              placeholder={placeholder}
              rows={rows}
              disabled={disabled}
              className={cn("resize-y", inputClassName)}
              onBlur={(e) => handleBlur(e, rhfOnBlur)}
              onChange={(e) => {
                rhfOnChange(e);
                props.onChange?.(e);
              }}
            />
          </div>

          {error ? (
            <div className="flex items-center gap-1 py-1">
              <span className="text-extrasmall text-red-500">
                {error?.message}
              </span>
            </div>
          ) : (
            extraInfo && (
              <div className="flex items-center gap-1 py-2">
                <span className="text-text-secondary-inverse text-extrasmall">
                  {extraInfo}
                </span>
              </div>
            )
          )}
        </div>
      )}
    />
  );
};

export default TextAreaControl;

export { TextAreaControl };
